import React, {
	FC,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useSearchParams } from "react-router-dom";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { UserContext, TranslationContext } from "@/app/providers";
import { useTaskPage, useTaskTableHeaderActions } from "@/features/task/hooks";
import { TaskGroup } from "@/features/task/model/Task/TaskRequest";
import {
	TaskDialog,
	useTaskHeaderActions,
} from "@/features/task/ui/components";
import { CustomToolbarV2, StyledDataGrid } from "@/shared/data-grid";
import { isImageAttachment } from "@/shared/lib/attachmentPreview";
import { DataGridCommentTooltip } from "@/shared/ui/DataGridCommentTooltip";
import { ImageAttachmentPreviewDialog } from "@/shared/ui/ImageAttachmentPreviewDialog";
import Spinner from "@/shared/ui/Spinner";
import ComponentError from "@/shared/ui/errors/Components";
import { useSelectedTaskGroups } from "@/storage/useSelectedTaskGroups";
import {
	sharedAutoHeightDataGridSectionSx,
	sharedTableCheckboxSx,
	sharedTableTagChipSx,
} from "@/styles/sharedDataGridStyles";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import {
	AccordionDetails,
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	Link,
	MenuItem,
	Select,
	Snackbar,
	TablePagination,
	TextField,
	Tooltip,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import rehypeRaw from "rehype-raw";

import { SortDirection } from "@/model/SortDirection";
import { UserSummary } from "@/model/User/UserSummary";

import { checkAdminAccess } from "@/service/ApplicationQueries";
import {
	useFetchTaskById,
	useFetchTaskGroups,
	useFetchTasks,
} from "@/service/TaskQueries";
import { useFetchUserSummariesByCurrentCompany } from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";


/* Day.js plugins (ensure once per bundle) */
dayjs.extend(utc);
dayjs.extend(timezonePlugin);

/* ───────────────────────── pastel palette to match previous screens ───────────────────────── */
const COL = {
	cardBg: (t: Theme) => t.palette.background.paper,
	cardBorder: (t: Theme) => t.palette.divider,
	headerBand: (t: Theme) =>
		t.palette.mode === "light" ? "#F7F7F9" : t.palette.background.default,
	rowAlt: (t: Theme) =>
		t.palette.mode === "light" ? "#FCFCFD" : t.palette.background.default,
	hover: (t: Theme) => (t.palette.mode === "light" ? "#F6F7FA" : "#1f2937"),
	chipBg: "#F1F5F9",
	tagBg: "#EEF2FF",
	tagText: "#3730A3",
};

interface TaskCommentDto {
	Date?: string | Date;
	Created?: string | Date;
	Value?: string;
	Text?: string;
}

interface TaskAttachmentDto {
	FileName?: string;
	Url?: string;
}

interface TaskUserDto {
	Id?: number;
	Name?: string;
	Email?: string;
}

interface TaskRecurringConfigurationDto {
	Frequency?: string;
	ShowBefore?: string;
}

interface TaskDto {
	Comments?: TaskCommentDto[];
	TaskId: number;
	Title: string;
	Description?: string;
	Deadline?: string | Date | null;
	ManualCheckList?: boolean;
	Reccuring?: boolean;
	ReccuringConfiguration?: TaskRecurringConfigurationDto | null;
	Shiftlog?: boolean;
	Handover?: boolean;
	Tags: string[];
	Attachment?: TaskAttachmentDto | null;
	User?: TaskUserDto | null;
	TaskGroupId: number;
	Status?: string;

	/* additional fields from payload */
	Completed?: string | Date | null;
	CompletedBy?: TaskUserDto | null;
	GroupResponsibleList?: number[];
	UserResponsibleList?: number[];
	SelectedSystem?: number | null;
	CompanyId?: number;
}

interface TasksResponse {
	Items: TaskDto[];
	Total: number;
	Page: number;
	PageSize: number;
	HasNextPage: boolean;
}

interface LegacyTasksResponse {
	Tasks?: TaskDto[];
	Total?: number;
}

interface TaskGroupsResponse {
	Groups: TaskGroup[];
}

type TaskGroupsPayload = TaskGroup[] | TaskGroupsResponse;
type TasksPayload = TasksResponse | LegacyTasksResponse;

const isTaskGroupsResponse = (
	payload: TaskGroupsPayload | undefined,
): payload is TaskGroupsResponse =>
	Boolean(
		payload &&
		typeof payload === "object" &&
		!Array.isArray(payload) &&
		Array.isArray(payload.Groups),
	);

const isTasksResponse = (
	payload: TasksPayload | undefined,
): payload is TasksResponse =>
	Boolean(
		payload &&
		typeof payload === "object" &&
		"Items" in payload &&
		Array.isArray(payload.Items),
	);

const isLegacyTasksResponse = (
	payload: TasksPayload | undefined,
): payload is LegacyTasksResponse =>
	Boolean(
		payload &&
		typeof payload === "object" &&
		"Tasks" in payload &&
		Array.isArray(payload.Tasks),
	);

const extractGridValue = (input: unknown): unknown => {
	if (typeof input === "object" && input !== null && "value" in input) {
		return (input as { value?: unknown }).value;
	}
	return input;
};

interface TaskRow {
	id: number;
	title: string;
	description: string;
	manualCheckList?: string;
	recurring: string;
	frequency: string;
	shiftlog: string;
	handover: string;
	tags: string[];
	user: string;
	attachment: TaskAttachmentDto | null;
	deadline?: string | Date | null;
	status: string;
	groupId: number;
	comments: TaskCommentDto[];

	/* enriched for UI */
	completed?: string | Date | null;
	completedBy?: TaskUserDto | null;
	groupResponsibleCount?: number;
	userResponsibleCount?: number;
	selectedSystem?: number | null;
	groupName?: string;
	responsibleGroups?: string[];
	responsibleUsers?: string[];
}

interface TaskCellParams {
	row: TaskRow;
	value?: unknown;
}

const formatDisplayName = (name?: string) => {
	const trimmedName = name?.trim();
	if (!trimmedName) return "";

	return trimmedName
		.toLowerCase()
		.replace(/(^|[\s._-])([a-z])/g, (_, separator: string, letter: string) => {
			return `${separator}${letter.toUpperCase()}`;
		});
};

const TaskCard: FC = () => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery("(max-width:767px)");
	const taskPage = useTaskPage();
	const hasTaskPage = Boolean(taskPage);
	const [searchParams, setSearchParams] = useSearchParams();
	const setTaskHeaderActions = useTaskHeaderActions();
	const sharedSearchInput = taskPage?.searchInput;
	const persistedTaskGroups = useSelectedTaskGroups();
	const selectedGroupIds = taskPage?.selectedGroupIds ?? persistedTaskGroups[0];
	const setSelectedGroupIds =
		taskPage?.setSelectedGroupIds ?? persistedTaskGroups[1];
	const sharedDateFrom = taskPage?.completedDateFrom;
	const sharedDateTo = taskPage?.completedDateTo;
	const sharedOnlyHandover = taskPage?.completedOnlyHandover;
	const sharedOnlyShiftlog = taskPage?.completedOnlyShiftlog;

	// Permissions
	const user = useContext(UserContext);
	const isAdmin = Boolean(user && checkAdminAccess(user));
	const useHeaderGridActions = hasTaskPage && isAdmin;

	// State
	const [searchTermTable, setSearchTermTable] = useState("");
	const [searchInput, setSearchInput] = useState(searchTermTable);

	const [showGroupWarning, setShowGroupWarning] = useState(false);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [dateFrom, setDateFrom] = useState<string>("");
	const [dateTo, setDateTo] = useState<string>("");

	// Sort state
	const [sortDirection, setSortDirection] = useState<SortDirection>(
		SortDirection.ASC,
	);

	const [filtersExpanded, setFiltersExpanded] = useState(false);

	// Quick toggles
	const [onlyHandover, setOnlyHandover] = useState(false);
	const [onlyShiftlog, setOnlyShiftlog] = useState(false);

	// Fetch task groups
	const { data: taskGroupsData } = useFetchTaskGroups() as {
		data?: TaskGroupsPayload;
	};
	const taskGroupsStabilised = useMemo(() => {
		if (Array.isArray(taskGroupsData)) return taskGroupsData;
		if (isTaskGroupsResponse(taskGroupsData)) return taskGroupsData.Groups;
		return [];
	}, [taskGroupsData]);
	const effectiveSelectedGroupIds = useMemo(() => {
		const availableGroupIdSet = new Set(
			taskGroupsStabilised
				.map((group) => Number(group.Id))
				.filter(
					(groupId): groupId is number =>
						Number.isInteger(groupId) && groupId > 0,
				),
		);
		const normalized = selectedGroupIds
			.map((groupId) => Number(groupId))
			.filter(
				(groupId): groupId is number =>
					Number.isInteger(groupId) && groupId > 0,
			);
		return [...new Set(normalized)].filter((groupId) =>
			availableGroupIdSet.has(groupId),
		);
	}, [selectedGroupIds, taskGroupsStabilised]);

	// Fetch user summaries for resolving responsible user names
	const { data: usersData = [] } = useFetchUserSummariesByCurrentCompany();
	const users: UserSummary[] = usersData;

	useEffect(() => {
		if (sharedSearchInput !== undefined) {
			setSearchInput(sharedSearchInput);
			setSearchTermTable(sharedSearchInput);
			setPage(1);
		}
	}, [sharedSearchInput]);

	useEffect(() => {
		if (sharedDateFrom !== undefined) {
			setDateFrom(sharedDateFrom);
			setPage(1);
		}
	}, [sharedDateFrom]);

	useEffect(() => {
		if (sharedDateTo !== undefined) {
			setDateTo(sharedDateTo);
			setPage(1);
		}
	}, [sharedDateTo]);

	useEffect(() => {
		if (sharedOnlyHandover !== undefined) {
			setOnlyHandover(sharedOnlyHandover);
			setPage(1);
		}
	}, [sharedOnlyHandover]);

	useEffect(() => {
		if (sharedOnlyShiftlog !== undefined) {
			setOnlyShiftlog(sharedOnlyShiftlog);
			setPage(1);
		}
	}, [sharedOnlyShiftlog]);

	useEffect(() => {
		if (taskGroupsStabilised.length === 0) return;

		const hasSelectionDiff =
			effectiveSelectedGroupIds.length !== selectedGroupIds.length ||
			effectiveSelectedGroupIds.some(
				(groupId, index) => groupId !== selectedGroupIds[index],
			);
		if (hasSelectionDiff) {
			setSelectedGroupIds(effectiveSelectedGroupIds);
		}
	}, [
		effectiveSelectedGroupIds,
		selectedGroupIds,
		setSelectedGroupIds,
		taskGroupsStabilised.length,
	]);

	// Stable dependency for useFetchTasks
	const stableGroupIds = useMemo(
		() => effectiveSelectedGroupIds,
		// joining ensures stable memo key
		[effectiveSelectedGroupIds.join(",")],
	);

	// Fetch archived tasks (server filtering by groups + searchTermTable)
	const { data: taskData, isFetching: isFetchingTasks } = useFetchTasks(
		stableGroupIds,
		"Archived",
		page,
		pageSize,
		searchTermTable,
		sortDirection,
	) as {
		data?: TasksPayload;
		isFetching: boolean;
	};

	useEffect(() => {
		setShowGroupWarning(stableGroupIds.length === 0);
	}, [stableGroupIds.length]);

	const taskItems = useMemo<TaskDto[]>(() => {
		if (isTasksResponse(taskData)) return taskData.Items;
		if (isLegacyTasksResponse(taskData)) return taskData.Tasks ?? [];
		return [];
	}, [taskData]);
	const userNameById = useMemo(
		() => new Map(users.map((candidate) => [candidate.Id, candidate.Name])),
		[users],
	);

	const mapTaskToDialogRow = useCallback(
		(task: TaskDto | null | undefined): TaskRow | null => {
			if (!task || !Number.isInteger(task.TaskId) || task.TaskId <= 0)
				return null;

			const groupName =
				taskGroupsStabilised.find((group) => group.Id === task.TaskGroupId)
					?.Name || "";
			const rawGroupCount = task.GroupResponsibleList?.length ?? 0;
			const rawUserCount = task.UserResponsibleList?.length ?? 0;
			const responsibleGroupNames: string[] =
				(rawGroupCount > 0
					? task.GroupResponsibleList
					: typeof task.TaskGroupId === "number"
						? [task.TaskGroupId]
						: []
				)
					?.map(
						(id: number) =>
							taskGroupsStabilised.find((group) => group.Id === id)?.Name,
					)
					.filter((name): name is string => Boolean(name)) ?? [];
			const responsibleUserNames: string[] =
				(rawUserCount > 0
					? task.UserResponsibleList
					: task.User?.Id
						? [task.User.Id]
						: []
				)
					?.map((id: number) => userNameById.get(id))
					.map((name) => formatDisplayName(name))
					.filter((name): name is string => Boolean(name)) ?? [];
			const assignedUserName = formatDisplayName(
				(task.User?.Id ? userNameById.get(task.User.Id) : undefined) ??
					task.User?.Name,
			);
			const completedByName = formatDisplayName(
				(task.CompletedBy?.Id ? userNameById.get(task.CompletedBy.Id) : undefined) ??
					task.CompletedBy?.Name,
			);

			return {
				id: task.TaskId,
				title: task.Title ?? "",
				description: task.Description ?? "",
				recurring: task.Reccuring ? "Yes" : "No",
				frequency: task.ReccuringConfiguration?.Frequency ?? "-",
				shiftlog: task.Shiftlog ? "Yes" : "No",
				handover: task.Handover ? "Yes" : "No",
				tags: task.Tags ?? [],
				user: assignedUserName,
				attachment: task.Attachment ?? null,
				deadline: task.Deadline ?? null,
				status: task.Status ?? "",
				groupId: task.TaskGroupId,
				comments: task.Comments ?? [],
				completed: task.Completed ?? null,
				completedBy: task.CompletedBy
					? {
						...task.CompletedBy,
						Name: completedByName,
					}
					: null,
				groupResponsibleCount:
					rawGroupCount > 0
						? rawGroupCount
						: typeof task.TaskGroupId === "number"
							? 1
							: 0,
				userResponsibleCount:
					rawUserCount > 0 ? rawUserCount : task.User?.Id ? 1 : 0,
				selectedSystem: task.SelectedSystem ?? null,
				groupName,
				responsibleGroups: responsibleGroupNames,
				responsibleUsers: responsibleUserNames,
			};
		},
		[taskGroupsStabilised, userNameById],
	);

	// Transform fetched task data into grid-ready format
	const taskRows: TaskRow[] = useMemo(() => {
		if (stableGroupIds.length === 0) return [];
		return taskItems
			.map((task) => mapTaskToDialogRow(task))
			.filter((row): row is TaskRow => row !== null);
	}, [mapTaskToDialogRow, stableGroupIds.length, taskItems]);

	const [newComment, setNewComment] = useState("");

	// Handle group reset (clear filter)
	const handleResetGroups = () => {
		setSelectedGroupIds([]);
		setPage(1);
		setShowGroupWarning(true);
	};
	const handleGroupChange = (event: SelectChangeEvent<number[]>) => {
		const value = event.target.value;
		const groupIds = Array.isArray(value)
			? value
				.map((id) => Number(id))
				.filter((id) => Number.isInteger(id) && id > 0)
			: [];
		setSelectedGroupIds(groupIds);
		setPage(1);
	};

	const { timezone } = useTime();

	const fmt = (d?: string | number | Date | dayjs.Dayjs | null) =>
		d ? dayjs.utc(d).tz(timezone).format("DD/MM/YY HH:mm:ss") : "";

	// Client-side filtering (inclusive dates on completed date, fallback deadline)
	const filteredRows = useMemo(() => {
		let rows = taskRows;

		if (onlyHandover) rows = rows.filter((r) => r.handover === "Yes");
		if (onlyShiftlog) rows = rows.filter((r) => r.shiftlog === "Yes");

		if (dateFrom) {
			const from = dayjs(dateFrom).startOf("day");
			rows = rows.filter((r) => {
				const baseDate = r.completed ?? r.deadline;
				return baseDate
					? dayjs(baseDate).isAfter(from) || dayjs(baseDate).isSame(from)
					: false;
			});
		}
		if (dateTo) {
			const to = dayjs(dateTo).endOf("day");
			rows = rows.filter((r) => {
				const baseDate = r.completed ?? r.deadline;
				return baseDate
					? dayjs(baseDate).isBefore(to) || dayjs(baseDate).isSame(to)
					: false;
			});
		}

		return rows;
	}, [taskRows, onlyHandover, onlyShiftlog, dateFrom, dateTo]);

	// Server-side paging: use backend Total to drive footer
	const totalItems =
		stableGroupIds.length === 0
			? 0
			: typeof taskData?.Total === "number"
				? taskData.Total
				: filteredRows.length;

	const fileNameExport = `ArchivedTasks_${dayjs().format("YYYY-MM-DD_HH-mm")}`;
	const [taskDialogOpen, setTaskDialogOpen] = useState(false);
	const [selectedTaskRow, setSelectedTaskRow] = useState<TaskRow | null>(null);
	const [imageAttachmentPreview, setImageAttachmentPreview] = useState<{
		url: string;
		fileName: string;
	} | null>(null);

	const taskIdFromUrl = useMemo(() => {
		const raw = searchParams.get("taskId");
		if (!raw) return null;
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	}, [searchParams]);
	const selectedTaskByIdQuery = useFetchTaskById(taskIdFromUrl ?? 0) as {
		data?: TaskDto;
	};
	const { data: selectedTaskById } = selectedTaskByIdQuery;

	const syncTaskIdInUrl = (taskId: number | null) => {
		const next = new URLSearchParams(searchParams);
		if (taskId == null) next.delete("taskId");
		else next.set("taskId", String(taskId));
		setSearchParams(next, { replace: true });
	};

	const setTaskDialogState = (open: boolean) => {
		setTaskDialogOpen(open);
		if (!open) {
			syncTaskIdInUrl(null);
			return;
		}
		if (selectedTaskRow?.id) {
			syncTaskIdInUrl(Number(selectedTaskRow.id));
		}
	};

	const openTaskDialogForRow = (row: TaskRow) => {
		setSelectedTaskRow(row);
		setTaskDialogOpen(true);
		syncTaskIdInUrl(Number(row.id));
	};

	// ── Highlight & text helpers ─────────────────────────────────────────────
	const highlight = (text?: string, term?: string) => {
		const t = (text ?? "").toString();
		const q = (term ?? "").trim().toLowerCase();
		if (!q) return t;
		const idx = t.toLowerCase().indexOf(q);
		if (idx === -1) return t;

		const before = t.slice(0, idx);
		const match = t.slice(idx, idx + q.length);
		const after = t.slice(idx + q.length);

		return (
			<>
				{before}
				<Box
					component="mark"
					sx={{
						px: 0.25,
						borderRadius: 0.5,
						backgroundColor: alpha(theme.palette.warning.main, 0.28),
					}}
				>
					{match}
				</Box>
				{after}
			</>
		);
	};

	useEffect(() => {
		if (taskIdFromUrl == null) {
			if (taskDialogOpen) setTaskDialogOpen(false);
			return;
		}

		const matchedRow = filteredRows.find(
			(row) => Number(row.id) === taskIdFromUrl,
		);
		if (!matchedRow) {
			const hydratedTask = mapTaskToDialogRow(selectedTaskById);
			if (!hydratedTask) return;

			if (!taskDialogOpen || selectedTaskRow?.id !== taskIdFromUrl) {
				setSelectedTaskRow(hydratedTask);
				setTaskDialogOpen(true);
			}
			return;
		}

		if (!taskDialogOpen || selectedTaskRow?.id !== taskIdFromUrl) {
			setSelectedTaskRow(matchedRow);
			setTaskDialogOpen(true);
		}
	}, [
		filteredRows,
		taskIdFromUrl,
		taskDialogOpen,
		selectedTaskRow?.id,
		selectedTaskById,
		mapTaskToDialogRow,
	]);

	const stripHtml = (s?: string) =>
		s
			? s
				.replace(/<[^>]*>/g, " ")
				.replace(/\s+/g, " ")
				.trim()
			: "";

	const truncate = (s: string, n = 160) =>
		s.length > n ? `${s.slice(0, n - 1)}…` : s;

	const asCommentsArray = (row: unknown): TaskCommentDto[] => {
		if (!row || typeof row !== "object") return [];
		const candidate = row as { comments?: unknown; Comments?: unknown };
		if (Array.isArray(candidate.comments)) {
			return candidate.comments as TaskCommentDto[];
		}
		if (Array.isArray(candidate.Comments)) {
			return candidate.Comments as TaskCommentDto[];
		}
		return [];
	};

	const renderMobileTaskCard = (row: TaskRow) => {
		const deadlineLabel = row.deadline ? fmt(row.deadline) : "No deadline";
		const completedLabel = row.completed ? fmt(row.completed) : "Not logged";
		const tags = Array.isArray(row.tags) ? row.tags : [];
		const quickFlags = [
			row.groupName || null,
			row.handover === "Yes" ? "Handover" : null,
			row.shiftlog === "Yes" ? "Shiftlog" : null,
			row.recurring === "Yes" ? "Recurring" : null,
		].filter(Boolean) as string[];

		return (
			<Card
				key={`mobile-${row.id}`}
				variant="outlined"
				onClick={() => openTaskDialogForRow(row)}
				sx={{
					borderRadius: 2,
					borderColor: alpha(theme.palette.divider, 0.9),
					bgcolor: "background.paper",
					cursor: "pointer",
					transition: "background-color 120ms ease, border-color 120ms ease",
					"&:active": {
						bgcolor: alpha(theme.palette.primary.main, 0.04),
					},
				}}
			>
				<Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "flex-start",
							justifyContent: "space-between",
							gap: 1,
						}}
					>
						<Box sx={{ minWidth: 0, flex: 1 }}>
							<Typography
								variant="subtitle2"
								sx={{
									fontWeight: 800,
									display: "-webkit-box",
									WebkitLineClamp: 2,
									WebkitBoxOrient: "vertical",
									overflow: "hidden",
								}}
							>
								{highlight(row.title, searchTermTable)}
							</Typography>
						</Box>
						<Box
							sx={{
								width: 32,
								height: 32,
								display: "grid",
								placeItems: "center",
								borderRadius: 2,
								bgcolor: alpha(theme.palette.primary.main, 0.08),
								color: "primary.main",
								flexShrink: 0,
							}}
						>
							<ArrowOutwardIcon sx={{ fontSize: 18 }} />
						</Box>
					</Box>

					<Typography
						variant="body2"
						color="text.secondary"
						sx={{
							display: "-webkit-box",
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
						}}
					>
						{truncate(stripHtml(row.description) || "No description", 120)}
					</Typography>

					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
						<Chip
							size="small"
							label={`Completed ${completedLabel}`}
							sx={{ borderRadius: 2, maxWidth: "100%" }}
						/>
						<Chip
							size="small"
							label={`Deadline ${deadlineLabel}`}
							sx={{ borderRadius: 2, maxWidth: "100%" }}
						/>
					</Box>

					<Typography variant="caption" color="text.secondary">
						{row.completedBy?.Name
							? `Closed by ${row.completedBy.Name}`
							: row.user || "Unassigned"}
					</Typography>

					{(quickFlags.length > 0 || tags.length > 0) && (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
							{quickFlags.map((flag) => (
								<Chip
									key={`${row.id}-${flag}`}
									size="small"
									label={flag}
									sx={{
										borderRadius: 2,
										bgcolor: alpha(theme.palette.primary.main, 0.08),
										color: "primary.main",
									}}
								/>
							))}
							{tags.slice(0, 2).map((tag) => (
								<Chip
									key={`${row.id}-${tag}`}
									size="small"
									label={tag}
									sx={{ borderRadius: 2 }}
								/>
							))}
							{tags.length > 2 && (
								<Chip
									size="small"
									label={`+${tags.length - 2}`}
									sx={{ borderRadius: 2 }}
								/>
							)}
						</Box>
					)}
				</Box>
			</Card>
		);
	};

	// Columns
	const columns = useMemo(
		() => [
			{
				field: "deadline",
				headerName: "Deadline",
				flex: 1.1,
				headerAlign: "left",
				align: "left",
				headerClassName: "leftAlignedColumnHeader",
				cellClassName: "leftAlignedCell",
				sortable: true,
				valueFormatter: (input: unknown) => {
					const value = extractGridValue(input);
					return fmt(
						typeof value === "string" || value instanceof Date ? value : null,
					);
				},
				renderCell: ({ row }: TaskCellParams) => {
					const deadline = row.deadline
						? dayjs.utc(row.deadline).tz(timezone)
						: null;
					const completedAt = row.completed
						? dayjs.utc(row.completed).tz(timezone)
						: null;
					const closedLate = Boolean(
						completedAt && deadline && completedAt.isAfter(deadline),
					);
					if (!deadline) {
						return <Typography variant="body2">—</Typography>;
					}
					const now = dayjs();
					const statusLabel = closedLate ? "Closed late" : "Closed";
					const statusColor = closedLate
						? theme.palette.warning.dark
						: theme.palette.success.dark;

					return (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								minWidth: 0,
								width: "100%",
								height: "100%",
								py: 0.125,
								textAlign: "left",
							}}
						>
							<Typography
								variant="caption"
								fontWeight={700}
								color="text.primary"
								sx={{ lineHeight: 1.15, letterSpacing: 0.1 }}
							>
								{deadline.format("DD/MM/YY HH:mm")}
							</Typography>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.4,
									mt: 0.3,
									minWidth: 0,
								}}
							>
								<Typography
									variant="caption"
									noWrap
									sx={{ fontWeight: 600, color: statusColor, lineHeight: 1 }}
								>
									{statusLabel}
								</Typography>
							</Box>
						</Box>
					);
				},
			},
			{
				field: "title",
				headerName: "Title",
				flex: 2.2,
				headerAlign: "left",
				align: "left",
				headerClassName: "leftAlignedColumnHeader",
				cellClassName: "leftAlignedCell",
				sortable: false,
				renderCell: ({ row }: TaskCellParams) => (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							minWidth: 0,
							width: "100%",
							height: "100%",
							py: 0.125,
							textAlign: "left",
						}}
					>
						<Typography
							variant="body2"
							fontWeight={700}
							noWrap
							sx={{ lineHeight: 1.25 }}
						>
							{highlight(row.title, searchTermTable)}
						</Typography>
					</Box>
				),
			},
				{
					field: "description",
					headerName: "Description",
					flex: 2.2,
					headerAlign: "left",
					align: "left",
					headerClassName: "leftAlignedColumnHeader",
					cellClassName: "leftAlignedCell",
					sortable: false,
				renderCell: ({ value, row }: TaskCellParams) => {
					const rawValue = typeof value === "string" ? value : "";
					const hasSearch = !!searchTermTable.trim();
					if (hasSearch) {
						const plain = stripHtml(rawValue);
						const preview = truncate(plain, 220);
						return (
							<Tooltip title={highlight(plain, searchTermTable)} arrow>
								<Box sx={{ width: "100%", minWidth: 0, textAlign: "left" }}>
									<Typography
										variant="body2"
										sx={{
											whiteSpace: "normal",
											display: "-webkit-box",
											WebkitLineClamp: 3,
											WebkitBoxOrient: "vertical",
											overflow: "hidden",
											textAlign: "left",
										}}
									>
										{highlight(preview, searchTermTable)}
									</Typography>
								</Box>
							</Tooltip>
						);
					}
					const plain = stripHtml(rawValue);
					const preview = truncate(plain, 220);
					return (
						<Tooltip
							title={
								<SATOSContentRender
									isNotProcedureRender={true}
									rehypePlugins={[rehypeRaw]}
									content={rawValue}
								/>
							}
							arrow
						>
							<Box sx={{ minWidth: 0, width: "100%", textAlign: "left" }}>
								<Typography
									variant="body2"
									sx={{
										display: "-webkit-box",
										WebkitLineClamp: 3,
										WebkitBoxOrient: "vertical",
										overflow: "hidden",
										lineHeight: 1.45,
										textAlign: "left",
									}}
								>
									{preview || "No description"}
								</Typography>
								{row?.attachment?.FileName ? (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ mt: 0.35, display: "block", textAlign: "left" }}
									>
										Attachment: {row.attachment.FileName}
									</Typography>
								) : null}
							</Box>
						</Tooltip>
					);
				},
			},
				{
					field: "completed",
					headerName: "Completed",
					flex: 1.2,
					sortable: false,
				valueFormatter: (input: unknown) => {
					const value = extractGridValue(input);
					return fmt(
						typeof value === "string" || value instanceof Date ? value : null,
					);
				},
					renderCell: ({ value }: TaskCellParams) => {
						const parsedValue =
							typeof value === "string" || value instanceof Date ? value : null;
						return (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									height: "100%",
									minWidth: 0,
									width: "100%",
									py: 0.125,
									textAlign: "center",
								}}
							>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ lineHeight: 1.15 }}
								>
									{parsedValue
										? dayjs
											.utc(parsedValue)
											.tz(timezone)
											.format("DD/MM/YY HH:mm")
										: "—"}
								</Typography>
							</Box>
						);
					},
				},
			{
				field: "recurring",
				headerName: "Recurring",
				flex: 0.8,
				sortable: false,
				renderCell: (params: TaskCellParams) => (
					<Checkbox
						checked={params.value === "Yes"}
						disabled
						size="small"
						sx={sharedTableCheckboxSx(theme)}
					/>
				),
			},
			{
				field: "attachment",
				headerName: "Attachment",
				flex: 1.4,
				sortable: false,
				renderCell: ({ row }: TaskCellParams) =>
					row.attachment?.Url ? (
						<Link
							href={row.attachment.Url}
							target={isImageAttachment(row.attachment) ? undefined : "_blank"}
							rel="noopener noreferrer"
							onClick={(event) => {
								if (!isImageAttachment(row.attachment)) return;
								event.preventDefault();
								setImageAttachmentPreview({
									url: row.attachment?.Url ?? "",
									fileName: row.attachment?.FileName ?? "Image attachment",
								});
							}}
							underline="hover"
							sx={{
								display: "inline-flex",
								alignItems: "center",
								minWidth: 0,
								overflow: "hidden",
							}}
						>
							<ArrowOutwardIcon fontSize="small" sx={{ mr: 0.5 }} />
							<Typography
								variant="body2"
								noWrap
								sx={{ textOverflow: "ellipsis" }}
							>
								{highlight(row.attachment.FileName ?? "", searchTermTable)}
							</Typography>
						</Link>
					) : (
						<Typography variant="body2" color="text.disabled">
							—
						</Typography>
					),
			},
			{
				field: "comments",
				headerName: "Comments",
				flex: 1.4,
				sortable: false,
				valueGetter: ({ row }: TaskCellParams) => {
					const list = asCommentsArray(row);
					if (!list.length) return "";
					return list
						.map((comment) => {
							const text = stripHtml(comment?.Text ?? comment?.Value ?? "");
							const when =
								comment?.Created || comment?.Date
									? dayjs
										.utc(comment.Created ?? comment.Date)
										.tz(timezone)
										.format("DD/MM/YY HH:mm:ss")
									: "";
							return when ? `${text} (${when})` : text;
						})
						.join(" | ");
				},
				renderCell: ({ row }: TaskCellParams) => {
					const list = asCommentsArray(row);
					if (!list.length) {
						return (
							<Typography variant="caption" color="text.disabled">
								<Translate String="Auto.Features.Task.Pages.CompletedTasks.L1124C60.NoComments">No comments</Translate>
							</Typography>
						);
					}
					const hasSearch = !!searchTermTable.trim();
					const tooltipItems = list.map((comment, index) => {
						const when =
							comment?.Date || comment?.Created
								? dayjs(comment.Date ?? comment.Created).format(
									"DD/MM/YY HH:mm:ss",
								)
								: "Unknown";

						if (hasSearch) {
							const plain = stripHtml((comment.Value ?? comment.Text) || "");
							const preview = truncate(plain, 220);

							return (
								<Box key={index}>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block", fontWeight: 700, mb: 0.5 }}
									>
										{when}
									</Typography>
									<Typography variant="body2" sx={{ whiteSpace: "normal" }}>
										{highlight(preview, searchTermTable)}
									</Typography>
								</Box>
							);
						}

						return (
							<Box key={index}>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", fontWeight: 700, mb: 0.5 }}
								>
									{when}
								</Typography>
								<Box
									sx={{
										fontSize: theme.typography.body2.fontSize,
										lineHeight: 1.45,
										"& p": { m: 0 },
										"& p + p": { mt: 0.5 },
										"& *": { maxWidth: "100%" },
										"& a": {
											wordBreak: "break-word",
											overflowWrap: "anywhere",
										},
									}}
								>
									<SATOSContentRender
										isNotProcedureRender={true}
										rehypePlugins={[rehypeRaw]}
										content={comment.Value ?? comment.Text}
									/>
								</Box>
							</Box>
						);
					});

					return <DataGridCommentTooltip items={tooltipItems} />;
				},
			},
			{
				field: "shiftlog",
				headerName: "Shiftlog",
				flex: 0.8,
				sortable: false,
				renderCell: (params: TaskCellParams) => (
					<Checkbox
						checked={params.value === "Yes"}
						disabled
						size="small"
						sx={sharedTableCheckboxSx(theme)}
					/>
				),
			},
			{
				field: "handover",
				headerName: "Handover",
				flex: 0.8,
				sortable: false,
				renderCell: (params: TaskCellParams) => (
					<Checkbox
						checked={params.value === "Yes"}
						disabled
						size="small"
						sx={sharedTableCheckboxSx(theme)}
					/>
				),
			},
			{
				field: "tags",
				headerName: "Tags",
				flex: 1.6,
				sortable: false,
				renderCell: ({ row }: TaskCellParams) => (
					<Box
						sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, minWidth: 0 }}
					>
						{row.tags.map((tag: string, idx: number) => (
							<Chip
								key={idx}
								label={
									<Typography variant="caption" noWrap>
										{highlight(tag, searchTermTable)}
									</Typography>
								}
								size="small"
								sx={sharedTableTagChipSx(theme)}
							/>
						))}
					</Box>
				),
			},
			{
				field: "user",
				headerName: "Name",
				flex: 1,
				headerAlign: "left",
				align: "left",
				headerClassName: "leftAlignedColumnHeader",
				cellClassName: "leftAlignedCell",
				sortable: false,
				renderCell: ({ row, value }: TaskCellParams) => (
					<Box sx={{ minWidth: 0, width: "100%", textAlign: "left" }}>
						<Typography variant="body2" fontWeight={700} noWrap>
							{highlight(
								typeof value === "string" ? value : "Unassigned",
								searchTermTable,
							)}
						</Typography>
						<Typography variant="caption" color="text.secondary" noWrap>
							{row.completedBy?.Name
								? `Closed by ${row.completedBy.Name}`
								: `${row.userResponsibleCount ?? 0} user${row.userResponsibleCount === 1 ? "" : "s"
								}`}
						</Typography>
					</Box>
				),
			},
		],
		[timezone, searchTermTable, theme],
	);
	const { apiRef, pdfDialog, toolbarProps } = useTaskTableHeaderActions({
		setTaskHeaderActions,
		isMobile,
		rows: stableGroupIds.length > 0 ? filteredRows : [],
		columns,
		fileName: fileNameExport,
		enableGridTools: useHeaderGridActions,
	});
	return (
		<Card
			elevation={0}
			sx={{
				flex: 1,
				minHeight: 0,
				borderRadius: 2,
				border: (t) => `1px solid ${t.palette.divider}`,
				m: { xs: 1, sm: 1.5, md: 2 },
				bgcolor: COL.cardBg(theme),
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<CardContent
				sx={{
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
					p: 2,
				}}
			>
				{/* Filters + Chart row */}
				<Grid
					container
					spacing={1.5}
					sx={{ flexShrink: 0, display: taskPage ? "none" : "flex" }}
				>
					{/* Left: Filters */}
					{!taskPage && (
						<Grid item xs={12} md={9}>
							<Button
								variant="contained"
								size="small"
								color="primary"
								fullWidth
								startIcon={<FilterAltIcon />}
								onClick={() => setFiltersExpanded((prev) => !prev)}
								sx={{
									display: { xs: "inline-flex", md: "none" },
									mb: 1,
									borderRadius: 999,
									fontWeight: 800,
									textTransform: "none",
								}}
							>
								{filtersExpanded ? (
									<Translate String="Tasks.Completed.HideFilters">
										Hide Filters
									</Translate>
								) : (
									<Translate String="Tasks.Completed.ShowFilters">
										Show Filters
									</Translate>
								)}
							</Button>

							<Box
								sx={{
									display: {
										xs: filtersExpanded ? "block" : "none",
										md: "block",
									},
									border: (t) => `1px solid ${t.palette.divider}`,
									borderRadius: 2,
									overflow: "hidden",
								}}
							>
								<Box
									sx={{
										px: 2,
										py: 1,
										bgcolor: COL.headerBand(theme),
										borderBottom: (t) => `1px solid ${t.palette.divider}`,
									}}
								>
									<Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
										<Translate String="CompletedTasks.Filters">
											Filters
										</Translate>
									</Typography>
								</Box>

								<AccordionDetails sx={{ p: 2 }}>
									<Grid container spacing={1.5}>
										{/* Group select + reset */}
										<Grid item xs={12} md={6}>
											<FormControl
												fullWidth
												size="small"
												error={stableGroupIds.length === 0}
												sx={{
													"& .MuiOutlinedInput-root": { borderRadius: 2 },
												}}
											>
												<InputLabel id="group-select-label">
													<Translate String="CompletedTasks.Group">
														Group
													</Translate>
												</InputLabel>
												<Select
													labelId="group-select-label"
													value={stableGroupIds}
													multiple
													label={
														<Translate String="CompletedTasks.Group">
															Group
														</Translate>
													}
													onChange={handleGroupChange}
													renderValue={(sel: number[]) => (
														<Box
															sx={{
																display: "flex",
																flexWrap: "wrap",
																gap: 0.5,
																maxWidth: "100%",
															}}
														>
															{sel.map((id) => {
																const groupName =
																	taskGroupsStabilised.find(
																		(group) => group.Id === id,
																	)?.Name || "";
																return (
																	<Chip
																		key={id}
																		label={groupName}
																		size="small"
																		sx={{
																			bgcolor: COL.chipBg,
																			fontWeight: 700,
																		}}
																	/>
																);
															})}
														</Box>
													)}
													MenuProps={{
														PaperProps: {
															elevation: 3,
															sx: { borderRadius: 2, mt: 0.5 },
														},
													}}
												>
													{taskGroupsStabilised.map((group) => (
														<MenuItem key={group.Id} value={group.Id}>
															{group.Name}
														</MenuItem>
													))}
												</Select>
												{stableGroupIds.length === 0 && (
													<FormHelperText>
														<Translate String="CompletedTasks.PleaseSelectAtLeastOneGroup">
															Please select at least one group
														</Translate>
													</FormHelperText>
												)}
											</FormControl>

											{stableGroupIds.length > 0 && (
												<Button
													variant="outlined"
													color="primary"
													size="small"
													onClick={handleResetGroups}
													startIcon={<RestartAltIcon />}
													sx={{
														mt: 1,
														borderRadius: 2,
														fontWeight: 800,
														textTransform: "none",
													}}
												>
													<Translate String="CompletedTasks.ResetGroups">
														Reset Groups
													</Translate>
												</Button>
											)}

											<Snackbar
												open={showGroupWarning}
												autoHideDuration={3000}
												onClose={() => setShowGroupWarning(false)}
												anchorOrigin={{
													vertical: "bottom",
													horizontal: "center",
												}}
											>
												<Alert
													onClose={() => setShowGroupWarning(false)}
													severity="warning"
													sx={{ width: "100%" }}
												>
													<Translate String="CompletedTasks.PleaseSelectAtLeastOneGroup">
														Please select at least one group.
													</Translate>
												</Alert>
											</Snackbar>
										</Grid>

										{/* Search + dates + quick chips */}
										<Grid item xs={12} md={6}>
											<Box display="flex" gap={1} mb={1}>
												<TextField
													fullWidth
													size="small"
													variant="outlined"
													disabled={
														stableGroupIds.length === 0 || isFetchingTasks
													}
													label={
														<Translate String="CompletedTasks.SearchTasks">
															Search Tasks
														</Translate>
													}
													value={searchInput}
													onChange={(e) => setSearchInput(e.target.value)}
													sx={{
														"& .MuiOutlinedInput-root": { borderRadius: 2 },
													}}
												/>
												<Button
													startIcon={<SearchIcon />}
													variant="outlined"
													color="primary"
													disabled={
														stableGroupIds.length === 0 || isFetchingTasks
													}
													onClick={() => {
														setSearchTermTable(searchInput);
														setPage(1);
													}}
													sx={{
														borderRadius: 2,
														fontWeight: 800,
														minWidth: 110,
													}}
												>
													<Translate String="CompletedTasks.Search">
														Search
													</Translate>
												</Button>
											</Box>

											<Box
												display="grid"
												gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
												gap={1}
											>
												<TextField
													label={
														<Translate String="CompletedTasks.DateFrom">
															Date From
														</Translate>
													}
													type="date"
													disabled={stableGroupIds.length === 0}
													InputLabelProps={{ shrink: true }}
													size="small"
													value={dateFrom}
													onChange={(e) => {
														setDateFrom(e.target.value);
														setPage(1);
													}}
													sx={{
														"& .MuiOutlinedInput-root": { borderRadius: 2 },
													}}
												/>
												<TextField
													label={
														<Translate String="CompletedTasks.DateTo">
															Date To
														</Translate>
													}
													type="date"
													disabled={stableGroupIds.length === 0}
													InputLabelProps={{ shrink: true }}
													size="small"
													value={dateTo}
													onChange={(e) => {
														setDateTo(e.target.value);
														setPage(1);
													}}
													sx={{
														"& .MuiOutlinedInput-root": { borderRadius: 2 },
													}}
												/>
											</Box>

											{/* Quick filter chips */}
											<Box mt={1} display="flex" gap={1} flexWrap="wrap">
												<Chip
													icon={<FilterAltIcon />}
													label={
														<Translate String="Tasks.Completed.HandoverOnly">
															Handover only
														</Translate>
													}
													clickable
													color={onlyHandover ? "primary" : "default"}
													onClick={() => {
														setOnlyHandover((v) => !v);
														setPage(1);
													}}
													sx={{ fontWeight: 700 }}
												/>
												<Chip
													icon={<FilterAltIcon />}
													label={
														<Translate String="Tasks.Completed.ShiftlogOnly">
															Shiftlog only
														</Translate>
													}
													clickable
													color={onlyShiftlog ? "primary" : "default"}
													onClick={() => {
														setOnlyShiftlog((v) => !v);
														setPage(1);
													}}
													sx={{ fontWeight: 700 }}
												/>
											</Box>
										</Grid>
									</Grid>
								</AccordionDetails>
							</Box>
						</Grid>
					)}
				</Grid>

				{/* Data Grid */}
				<Card
					variant="outlined"
					sx={{
						flex: 1,
						minHeight: 0,
						borderRadius: 2,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
						mb: 1.5,
					}}
				>
					<Box
						sx={{
							...sharedAutoHeightDataGridSectionSx(theme),
						}}
					>
						<ErrorBoundary
							fallbackRender={({ error, resetErrorBoundary }) => (
								<ComponentError
									message={
										error instanceof Error
											? error.message
											: "Something went wrong."
									}
									reset={resetErrorBoundary}
								/>
							)}
						>
							<Suspense fallback={<Spinner text={GetLanguageValueAsString(translation, "Auto.Features.Task.Pages.CompletedTasks.L1645C42.Loading", "Loading")} />}>
								<Box
									sx={{
										flex: 1,
										minHeight: 0,
										display: "flex",
										flexDirection: "column",
									}}
								>
									<Box
										sx={{
											width: "100%",
										}}
									>
										{isMobile ? (
											<Box
												sx={{
													width: "100%",
													display: "flex",
													flexDirection: "column",
													gap: 1,
													p: 0.25,
												}}
											>
												{(stableGroupIds.length > 0 ? filteredRows : []).map(
													(row) => renderMobileTaskCard(row),
												)}
											</Box>
										) : (
											<StyledDataGrid
												apiRef={apiRef}
												autoHeight
												showToolbar={isAdmin}
												sortingMode="server"
												sortingOrder={["asc", "desc"]}
												sortModel={[
													{
														field: "deadline",
														sort:
															sortDirection === SortDirection.DESC
																? "desc"
																: "asc",
													},
												]}
												onSortModelChange={(model) => {
													if (
														model.length > 0 &&
														model[0].field === "deadline"
													) {
														setSortDirection(
															model[0].sort === "desc"
																? SortDirection.DESC
																: SortDirection.ASC,
														);
														setPage(1);
													}
												}}
												rows={stableGroupIds.length > 0 ? filteredRows : []}
												rowCount={totalItems}
												columns={columns}
												getRowHeight={() => "auto"}
												disableVirtualization
												getRowClassName={(params) => {
													const index = params.indexRelativeToCurrentPage ?? 0;
													return index % 2 === 0 ? "row-even" : "row-odd";
												}}
												hideFooterPagination
												loading={isFetchingTasks}
												disableRowSelectionOnClick
												onRowClick={(params) => {
													openTaskDialogForRow(params.row as TaskRow);
												}}
												slots={
													isAdmin ? { toolbar: CustomToolbarV2 } : undefined
												}
												slotProps={
													useHeaderGridActions
														? { toolbar: toolbarProps }
														: undefined
												}
													sx={{
														width: "100%",
														maxWidth: "100%",
														"& .MuiDataGrid-columnHeader": {
															justifyContent: "center",
														},
														"& .MuiDataGrid-columnHeaderTitleContainer": {
															justifyContent: "center",
														},
														"& .MuiDataGrid-columnHeaderTitleContainerContent": {
															justifyContent: "center",
															width: "100%",
														},
														"& .MuiDataGrid-columnHeaderTitle": {
															width: "100%",
															textAlign: "center",
														},
														"& .MuiDataGrid-cell": {
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															textAlign: "center",
														},
														"& .leftAlignedColumnHeader": {
															justifyContent: "flex-start",
														},
														"& .leftAlignedColumnHeader .MuiDataGrid-columnHeaderTitleContainer": {
															justifyContent: "flex-start",
														},
														"& .leftAlignedColumnHeader .MuiDataGrid-columnHeaderTitleContainerContent": {
															justifyContent: "flex-start",
														},
														"& .leftAlignedColumnHeader .MuiDataGrid-columnHeaderTitle": {
															textAlign: "left",
														},
														"& .leftAlignedCell": {
															justifyContent: "flex-start",
															textAlign: "left",
														},
													}}
												/>
										)}
									</Box>

									{/* Empty state */}
									{!isFetchingTasks &&
										stableGroupIds.length > 0 &&
										filteredRows.length === 0 && (
											<Box
												sx={{
													display: "grid",
													placeItems: "center",
													py: 8,
													textAlign: "center",
													color: "text.secondary",
												}}
											>
												<Box>
													<Typography
														variant="h6"
														sx={{ fontWeight: 800, mb: 0.5 }}
													>
														<Translate String="CompletedTasks.NoArchivedTasksFound">
															No archived tasks found
														</Translate>
													</Typography>
													<Typography variant="body2">
														<Translate String="CompletedTasks.TryAdjustingFiltersOrYourSearchTerm">
															Try adjusting filters or your search term.
														</Translate>
													</Typography>
												</Box>
											</Box>
										)}
								</Box>
							</Suspense>
						</ErrorBoundary>
					</Box>
				</Card>
			</CardContent>

			{/* Match Shift pagination footer */}
			{totalItems > 0 && (
				<Box sx={{ px: 2, pb: 2, flexShrink: 0 }}>
					<TablePagination
						component="div"
						count={totalItems}
						page={page - 1}
						onPageChange={(_event, newPage) => setPage(newPage + 1)}
						rowsPerPage={pageSize}
						onRowsPerPageChange={(event) => {
							const newSize = Number(event.target.value);
							if (newSize !== pageSize) {
								setPageSize(newSize);
								setPage(1);
							}
						}}
						rowsPerPageOptions={[10, 25, 50, 100]}
						SelectProps={{ size: "small" }}
						labelRowsPerPage={
							<Translate String="Shift.PaginationRows">
								Rows per page:
							</Translate>
						}
						labelDisplayedRows={({ from, to, count }) =>
							count !== -1 ? `${from}–${to} of ${count}` : `${from}–${to}`
						}
						showFirstButton
						showLastButton
						sx={{
							flexShrink: 0,
							borderTop: (t) => `1px solid ${t.palette.divider}`,
							bgcolor: "background.default",
							minHeight: 28,
							"& .MuiTablePagination-toolbar": {
								minHeight: 28,
								px: 1.5,
								py: 0,
							},
							"& .MuiTablePagination-selectLabel": {
								mr: 0.5,
								display: { xs: "none", sm: "block" },
							},
							"& .MuiTablePagination-displayedRows": {
								fontSize: { xs: "0.75rem", sm: "inherit" },
							},
							"& .MuiTablePagination-select": {
								py: 0,
								px: 0.75,
								minHeight: 22,
							},
							"& .MuiTablePagination-actions": { ml: 0 },
							"& .MuiIconButton-root": {
								padding: 2,
								"& svg": { fontSize: 16 },
							},
						}}
					/>
				</Box>
			)}

			{/* Task Info Dialog (view-only) */}
			<TaskDialog
				taskDialogOpen={taskDialogOpen}
				selectedTask={selectedTaskRow}
				setTaskDialogOpen={setTaskDialogState}
				renderInfoItem={(label, value) => (
					<Box>
						<Typography variant="subtitle2" color="textSecondary">
							{label}
						</Typography>
						<Typography>{value}</Typography>
					</Box>
				)}
				newComment={newComment}
				setNewComment={setNewComment}
				isAdmin={isAdmin}
				viewOnly={true}
			/>
			<ImageAttachmentPreviewDialog
				preview={imageAttachmentPreview}
				onClose={() => setImageAttachmentPreview(null)}
			/>
			{pdfDialog}
		</Card>
	);
};

const CompletedTasks: FC = () => (
	<Box
		sx={{
			width: "100%",
			height: "100%",
			flex: 1,
			minHeight: 0,
			display: "flex",
		}}
	>
		<TaskCard />
	</Box>
);

export default CompletedTasks;
